import type { RegexReplace } from "./personation-context";

const MAX_REGEX_PATTERN_LENGTH = 200;
const MAX_REGEX_REPLACES = 20;

/**
 * Whether a `fetch_url` points to a pdf, by its own filename extension.
 *
 * Only ever applied to `fetch_url` (our own proxy address, whose filename we
 * control) -- NEVER to the public/display `url`, which can be anything
 * (a redirector, a CMS page, a shortened link) and would misclassify. Our
 * `/static` proxy exists for any Cruddy-internal file, not just pdfs, so
 * `fetch_url` alone does not mean "pdf" -- this is the second half of that
 * check, alongside its presence.
 */
export function isPdfUrl(url: string): boolean {
  try {
    const { pathname } = new URL(url);
    return pathname.toLowerCase().endsWith(".pdf");
  } catch {
    return false;
  }
}

/**
 * The page number stamped on a PDF address (`?page=N`, written by
 * `stamp_page_into_pdf_url` in commonlib), or `null` when the address
 * carries none, or an invalid one.
 */
export function pageNumberFromPdfUrl(url: string): number | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const raw = parsed.searchParams.get("page");
  if (!raw) return null;

  const page = Number.parseInt(raw, 10);
  return Number.isInteger(page) && page > 0 ? page : null;
}

/**
 * The same URL with the `page` query param removed, keeping every other
 * param intact. Used to normalize a PDF URL before it reaches the
 * authenticated proxy (which doesn't know `page`) or the recent-pages
 * history (so citations to the same document don't fan out into
 * duplicate entries per page).
 */
export function stripPageParam(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }

  parsed.searchParams.delete("page");
  return parsed.toString();
}

export function applyLinkRegexReplaces(
  url: string,
  replaces: RegexReplace[]
): string {
  if (!url || !replaces || replaces.length === 0) return url;

  return replaces.slice(0, MAX_REGEX_REPLACES).reduce((acc, { pattern, replacement }) => {
    try {
      if (pattern.length > MAX_REGEX_PATTERN_LENGTH) {
        console.warn(`[Fiinder] Regex pattern too long (max ${MAX_REGEX_PATTERN_LENGTH}): ${pattern.slice(0, 50)}...`);
        return acc;
      }
      const regex = new RegExp(pattern, "g");
      return acc.replace(regex, replacement);
    } catch {
      console.warn(`[Fiinder] Invalid regex pattern: ${pattern}`);
      return acc;
    }
  }, url);
}
